<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import DegreeContent from './DegreeContent.vue'
import { useStore } from 'vuex'
import { IExtendedDegree } from '@shared/types/extensions'
import { IDegreePackage } from '@shared/types'
import { DegreeService } from '@/services/degreeService'
import { ref as dbRef, onValue, off } from 'firebase/database'
import { database } from '@/firebase/config'

const props = defineProps<{
	modelValue: boolean
	degree: IExtendedDegree
	testMode: boolean
	institutionId: string
	packageId: number
}>()
const db = database
const degreeService = new DegreeService()
const emit = defineEmits(['update:modelValue', 'degree-updated'])
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})

const store = useStore()
const loading = ref(false)
const localDegree = ref<IExtendedDegree | null>(null)
const localPackage = ref<IDegreePackage | null>(null)
const getInstitutionPath = (path: string) => `institutions/${props.institutionId}${props.testMode ? '/testMode' : ''}${path}`
const handleError = (error: unknown, message: string) => {
	console.error(message, error)
	store.dispatch('notification/showError', message)
}

const handleSuccess = (message: string) => {
	store.dispatch('notification/showSuccess', message)
}
const getDataPackage = (packageId: number) => {
	// Validar que el packageId sea válido
	if (!packageId || packageId <= 0) {
		localPackage.value = null
		return
	}

	// Obtener los datos del paquete actual - usar la ruta correcta para degrees
	const packageRef = dbRef(db, getInstitutionPath(`/degrees/packages/${packageId}`))

	// Limpiar listener anterior
	off(packageRef)

	onValue(
		packageRef,
		snapshot => {
			if (snapshot.exists()) {
				const packageInfo = snapshot.val()
				localPackage.value = packageInfo
			} else {
				localPackage.value = null
			}
		},
		error => {
			console.error('Error al obtener datos del paquete:', error)
			localPackage.value = null
		}
	)
}

const canDownloadXml = computed(() => props.degree?.xml)
const canDownloadResponse = computed(() => localPackage.value?.sepResult && localPackage.value.sepResult.length > 0)
const canCancelDegree = computed(() => localPackage.value?.procesedBySep === true)
const canGeneratePdf = ref(Boolean(props.degree?.xmlAuthenticated))
const getZip = (id: string) => {
	degreeService.getZipOfDegree({
		degree: id,
		institution: props.institutionId,
		testMode: props.testMode
	})
}
const downloadResponse = (packageId: number) => {
	if (!packageId || !localPackage.value?.sepResult || localPackage.value.sepResult.length === 0) {
		handleError(null, 'No hay datos de respuesta disponibles para descargar')
		return
	}

	const path = `/private/institutions/${props.institutionId + (props.testMode ? '/testMode' : '')}/packages/${packageId}`
	const dataPackagePrivate = dbRef(db, path)

	onValue(
		dataPackagePrivate,
		snapshot => {
			if (snapshot.exists()) {
				const dataPackagePrivate = snapshot.val()

				// Verificar si existe zipBase64
				if (dataPackagePrivate?.sentData?.descargaTituloElectronico?.titulosBase64) {
					try {
						// Convertir base64 a blob
						const binaryString = atob(dataPackagePrivate.sentData.descargaTituloElectronico.titulosBase64)
						const bytes = new Uint8Array(binaryString.length)
						for (let i = 0; i < binaryString.length; i++) {
							bytes[i] = binaryString.charCodeAt(i)
						}

						// Crear blob del archivo ZIP
						const zipBlob = new Blob([bytes], { type: 'application/zip' })

						// Crear URL y descargar
						const url = window.URL.createObjectURL(zipBlob)
						const link = document.createElement('a')
						link.href = url
						link.download = `Respuesta_SEP_Paquete_${packageId}_${new Date().toISOString().slice(0, 10)}.zip`

						document.body.appendChild(link)
						link.click()
						document.body.removeChild(link)
						window.URL.revokeObjectURL(url)

						handleSuccess('Archivo ZIP de respuesta descargado exitosamente')
					} catch (error) {
						console.error('Error al procesar ZIP base64:', error)
						handleError(error, 'Error al procesar el archivo ZIP')
					}
				} else {
					handleError(null, 'No se encontró el archivo ZIP en la respuesta')
				}
			} else {
				handleError(null, 'No se encontraron datos del paquete privado')
			}
		},
		error => {
			console.error('Error al obtener datos del paquete privado:', error)
			handleError(error, 'Error al obtener datos del paquete')
		}
	)
}

const cancelDegree = async () => {
	try {
		const folioControl = props.degree?.folioControl
		if (!folioControl) {
			handleError(null, 'No se pudo identificar el folio de control')
			return
		}

		if (!confirm(`¿Está seguro de cancelar el título con folio de control ${folioControl}?`)) {
			return
		}

		loading.value = true

		const response = await degreeService.cancelDegree(props.institutionId, folioControl, props.testMode)

		if (response.code === 1) {
			handleSuccess(response.message || 'Título cancelado correctamente')
			emit('degree-updated')
		} else {
			handleError(null, response.message || 'Ocurrió un error al cancelar el título')
		}
	} catch (error) {
		handleError(error, 'Ocurrió un error al cancelar el título')
	} finally {
		loading.value = false
	}
}

const closeModalDetails = () => {
	showModal.value = false
	// Limpiar datos del paquete al cerrar el modal
	localPackage.value = null
}
const generatePdf = async () => {
	try {
		if (!props.degree?.folioControl) {
			handleError(null, 'No se pudo identificar el folio de control')
			return
		}

		if (!confirm(`¿Desea generar el PDF para el título con folio de control ${props.degree.folioControl}?`)) {
			return
		}

		loading.value = true

		const response = await degreeService.degreeToPdf(props.institutionId, props.degree.folioControl)
		// Verificar si la respuesta contiene datos del PDF
		if (response && response.data && response.data.body && response.data.body.data) {
			const fileArrayBuffer = new Uint8Array(response.data.body.data)
			const pdfBlob = new Blob([fileArrayBuffer], {
				type: 'application/pdf'
			})
			downloadFile(pdfBlob, `${props.degree.controlNumber}.pdf`, 'application/pdf')
		} else {
			handleError(null, 'No se pudo generar el PDF del título')
		}
	} catch (error) {
		console.error('Error al generar PDF:', error)
		handleError(null, 'Ocurrió un error al generar PDF del título')
	} finally {
		loading.value = false
	}
}
const uploadAuthenticatedXml = async () => {
	try {
		const fileInput = document.createElement('input')
		fileInput.type = 'file'
		fileInput.accept = '.xml'

		fileInput.onchange = async event => {
			const target = event.target as HTMLInputElement
			if (!target.files || target.files.length === 0) return

			const file = target.files[0]
			const reader = new FileReader()

			reader.onload = async e => {
				try {
					loading.value = true
					const base64Xml = (e.target?.result as string).split(',')[1]

					if (!props.degree?.folioControl) {
						handleError(null, 'No se pudo identificar el folio de control')
						return
					}

					const response = await degreeService.uploadAuthenticatedXml(props.institutionId, props.degree.folioControl, props.testMode, base64Xml)

					if (response.code === 1) {
						handleSuccess(response.message || 'XML autenticado recibido correctamente')
						canGeneratePdf.value = true
						emit('degree-updated')
					} else {
						handleError(null, response.message || 'Ocurrió un error al subir el XML autenticado')
					}
				} catch (error) {
					handleError(error, (error as Error).message || 'Ocurrió un error al subir el XML autenticado')
				} finally {
					loading.value = false
				}
			}

			reader.readAsDataURL(file)
		}

		fileInput.click()
	} catch (error) {
		handleError(error, 'Ocurrió un error al preparar la subida del XML')
	}
}
const createBlobFromBase64 = (base64Data: string, mimeType: string): Blob => {
	try {
		const byteCharacters = atob(base64Data)
		const byteArrays = []

		for (let offset = 0; offset < byteCharacters.length; offset += 512) {
			const slice = byteCharacters.slice(offset, offset + 512)
			const byteNumbers = Array.from({ length: slice.length }, (_, i) => slice.charCodeAt(i))

			byteArrays.push(new Uint8Array(byteNumbers))
		}

		return new Blob(byteArrays, { type: mimeType })
	} catch (error) {
		console.error('Error al procesar base64:', error)
		return new Blob([base64Data], { type: mimeType })
	}
}

const downloadFile = (data: string | Blob, fileName: string, mimeType: string) => {
	try {
		const blob = typeof data === 'string' ? createBlobFromBase64(data, mimeType) : data

		const url = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = fileName
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		URL.revokeObjectURL(url)
	} catch (error) {
		handleError(error, 'Error al descargar el archivo')
	}
}
watch(
	() => props.degree,
	newDegree => {
		localDegree.value = newDegree ? { ...newDegree } : null
		if (localDegree.value && localDegree.value.package && localDegree.value.package > 0) {
			getDataPackage(localDegree.value.package)
		}
	},
	{ immediate: true }
)
watch(
	() => props.modelValue,
	newModelValue => {
		if (newModelValue && props.packageId && props.packageId > 0) {
			getDataPackage(props.packageId)
		}
	},
	{ immediate: true }
)

// Watcher adicional para cuando cambie el packageId
watch(
	() => props.packageId,
	newPackageId => {
		if (props.modelValue && newPackageId && newPackageId > 0) {
			getDataPackage(newPackageId)
		}
	}
)

// Watcher para limpiar listeners cuando se cierre el modal
watch(
	() => props.modelValue,
	newModelValue => {
		if (!newModelValue) {
			// Limpiar listeners cuando se cierre el modal
			const packageRef = dbRef(db, getInstitutionPath(`/degrees/packages/${props.packageId}`))
			off(packageRef)
		}
	}
)
</script>
<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss background-color="transparent">
		<VaCard v-if="degree" class="max-w-4xl mx-auto">
			<VaCardTitle :class="[props.testMode ? 'bg-gradient-paquete-test' : 'bg-gradient-paquete', 'rounded-t-xl']">
				<div class="row align-items-center">
					<div class="col-md-11 text-left">
						<VaIcon name="school" class="text-white" size="large" />
						<div>
							<h2 class="text-xl font-bold text-white m-0">Detalles del Título</h2>
							<p class="text-sm text-blue-100 m-0 text-white">Información completa del título</p>
						</div>
					</div>
					<div class="col-md-1 text-right ml2">
						<VaButton round icon="cancel" :color="!props.testMode ? '#0d6efd' : '#7f1d1d'" type="button" @click="closeModalDetails" style="color: white; font-weight: bold" />
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="p-6">
				<!-- Contenido del Título -->
				<div class="mb-6">
					<div class="section-title">
						<VaIcon name="description" class="title-icon" />
						Contenido del Título
					</div>
					<div class="package-card">
						<template v-if="degree">
							<DegreeContent :degree="degree" />
						</template>
						<template v-else>
							<div class="p-8 text-center">
								<VaIcon name="description" class="text-4xl text-gray-400 mb-4" />
								<p class="text-gray-500">No hay información disponible</p>
							</div>
						</template>
					</div>
				</div>

				<!-- Acciones -->
				<div class="mb-6">
					<div class="section-title">
						<VaIcon name="build" class="title-icon" />
						Acciones Disponibles
					</div>
					<div class="package-card">
						<!-- Botones principales -->
						<div class="row g-3">
							<div class="col-md-4">
								<button @click="getZip(degree.id)" class="btn btn-primary w-100 gap-2" :disabled="!canDownloadXml">
									<VaIcon name="file_download" />
									<span>Descargar XML</span>
								</button>
							</div>
							<div class="col-md-4">
								<button v-if="localPackage && localPackage.procesedBySep" @click="downloadResponse(localPackage?.id)" class="btn btn-success w-100 gap-2" :disabled="!canDownloadResponse">
									<VaIcon name="cloud_download" />
									<span>Descargar Respuesta</span>
								</button>
							</div>
							<div class="col-md-4">
								<button @click="cancelDegree" class="btn btn-danger w-100 gap-2" :disabled="!canCancelDegree">
									<VaIcon name="cancel" />
									<span>Cancelar Título</span>
								</button>
							</div>
						</div>

						<!-- Botones secundarios -->
						<div class="row g-3 mt-3">
							<div class="col-md-6">
								<button @click="uploadAuthenticatedXml" class="btn btn-outline-success w-100 gap-2">
									<VaIcon name="upload_file" />
									<span>Cargar XML Autenticado</span>
								</button>
							</div>
							<div class="col-md-6">
								<button @click="generatePdf" class="btn btn-outline-secondary w-100 gap-2" :disabled="!canGeneratePdf">
									<VaIcon name="picture_as_pdf" />
									<span>Generar PDF</span>
								</button>
							</div>
						</div>
					</div>
				</div>

				<!-- Información del Estado -->
				<div v-if="localPackage" class="mb-6">
					<div class="section-title">
						<VaIcon name="info" class="title-icon" />
						Estado del Procesamiento
					</div>
					<div class="package-card">
						<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div class="info-item">
								<VaIcon name="check_circle" class="info-icon" />
								<span class="info-label">Procesado por SEP:</span>
								<span class="info-value">{{ localPackage.procesedBySep ? 'Sí' : 'No' }}</span>
							</div>
						</div>
					</div>
				</div>
			</VaCardContent>
		</VaCard>
	</VaModal>
</template>

<style scoped>
/* Estilos base del modal */
.max-w-4xl {
	max-width: 56rem;
}

/* Títulos de sección */
.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.title-icon {
	color: #007bff;
	font-size: 1.25rem;
}

/* Tarjetas base */
.package-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
}

.package-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

/* Elementos de información */
.info-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem;
	background: #f8f9fa;
	border-radius: 8px;
	border-left: 4px solid #007bff;
}

.info-icon {
	color: #007bff;
	font-size: 1.125rem;
	flex-shrink: 0;
}

.info-label {
	font-weight: 600;
	color: #1a2a3a;
	min-width: 120px;
}

.info-value {
	color: #6c757d;
	flex: 1;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.75rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover:not(:disabled) {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-success {
	background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
	color: white;
}

.btn-success:hover:not(:disabled) {
	background: linear-gradient(135deg, #1e7e34 0%, #155724 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-danger {
	background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
	color: white;
}

.btn-danger:hover:not(:disabled) {
	background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
	transform: none !important;
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive design */
@media (max-width: 768px) {
	.info-item {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.info-label {
		min-width: auto;
	}

	.section-title {
		font-size: 1.125rem;
	}
}

/* Utilidades */
.space-y-6 > * + * {
	margin-top: 1.5rem;
}

.mb-4 {
	margin-bottom: 1rem;
}

.mb-6 {
	margin-bottom: 1.5rem;
}

.p-6 {
	padding: 1.5rem;
}

.gap-2 {
	gap: 0.5rem;
}

.gap-4 {
	gap: 1rem;
}

.text-center {
	text-align: center;
}

.text-white {
	color: white;
}

.font-bold {
	font-weight: 700;
}

.text-xl {
	font-size: 1.25rem;
}

.text-sm {
	font-size: 0.875rem;
}

.m-0 {
	margin: 0;
}

/* Gradientes para el header */
.bg-gradient-paquete {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}

.bg-gradient-paquete-test {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}
</style>
